/**
 * Time rendering for the Field pages, which speak UTC and nothing else.
 *
 * Hub schedules are cron in UTC, S3 acquisition timestamps are UTC, and the dot
 * grid's day columns are cut on UTC midnight. A stamp shown in the viewer's own
 * zone would sit next to that grid quietly disagreeing with it — and would read
 * differently depending on who opened the page. So every instant the backend
 * hands us (always an ISO string) comes back out through here, in UTC and
 * labelled as such.
 *
 * Zones only ever reach the display: comparing two instants (`Date.parse` vs
 * `Date.now()`) is zone-free, and that is how the page decides what is past.
 */

import { useEffect, useState } from 'react';

const pad2 = (n: number) => String(n).padStart(2, '0');

/**
 * How far past a reported wake-up we wait before calling it missed. The stamp
 * is the hub's own clock and the comparison is the viewer's, so a couple of
 * minutes either way proves nothing — and a hub that is right now waking up
 * and dialling in should not flash red at whoever happens to be looking.
 */
export const WAKE_UP_GRACE_MS = 10 * 60 * 1000;

/**
 * An instant as `2026-08-05 12:00 UTC`, or with `seconds` as
 * `2026-08-05 12:00:34 UTC`. Empty string for anything unparseable, so a bad
 * value from the backend shows as blank rather than "Invalid Date".
 */
export function utcStamp(iso: string | Date, seconds = false): string {
    const date = typeof iso === 'string' ? new Date(iso) : iso;
    if (Number.isNaN(date.getTime())) return '';
    const time =
        `${pad2(date.getUTCHours())}:${pad2(date.getUTCMinutes())}` +
        (seconds ? `:${pad2(date.getUTCSeconds())}` : '');
    return (
        `${date.getUTCFullYear()}-${pad2(date.getUTCMonth() + 1)}-` +
        `${pad2(date.getUTCDate())} ${time} UTC`
    );
}

/**
 * An elapsed time as `3d 4h`, `4h 12m` or `7m` — how late something is, at the
 * precision that tells you whether to worry. Never negative.
 */
export function roughDuration(ms: number): string {
    const minutes = Math.max(0, Math.floor(ms / 60_000));
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);
    if (days > 0) return `${days}d ${hours % 24}h`;
    if (hours > 0) return `${hours}h ${pad2(minutes % 60)}m`;
    return `${minutes}m`;
}

/**
 * Now, re-read every `everyMs`. The monitoring data is fetched once and then
 * sits there; without this, a wake-up would still read as "in the future" long
 * after it went past, on a tab nobody has reloaded.
 */
export function useNow(everyMs: number): number {
    const [now, setNow] = useState(() => Date.now());
    useEffect(() => {
        const timer = setInterval(() => setNow(Date.now()), everyMs);
        return () => clearInterval(timer);
    }, [everyMs]);
    return now;
}

/** An instant as a `datetime-local` value (`YYYY-MM-DDTHH:mm`), in UTC. */
export function utcInputValue(date: Date): string {
    return (
        `${date.getUTCFullYear()}-${pad2(date.getUTCMonth() + 1)}-` +
        `${pad2(date.getUTCDate())}T` +
        `${pad2(date.getUTCHours())}:${pad2(date.getUTCMinutes())}`
    );
}

/**
 * A `datetime-local` value read back as UTC — the zone the picker itself has no
 * notion of, and which the browser would otherwise resolve as local wall time.
 * Null when the field is empty or holds nothing usable.
 */
export function parseUtcInput(value: string): Date | null {
    if (!value) return null;
    // `YYYY-MM-DDTHH:mm`, or `:ss` too if the picker was given a finer step.
    const date = new Date(`${value}${value.length === 16 ? ':00' : ''}Z`);
    return Number.isNaN(date.getTime()) ? null : date;
}
